import React, { useEffect, useRef, useState } from 'react';

import api from '../../../api';
import { listAvatarCreations } from '../services/avatarApi';
import '../styles/AvatarPostSelector.css';


function errorMessage(error) {
  return (
    error?.response?.data?.error ||
    error?.message ||
    'Something went wrong. Please try again.'
  );
}

export default function AvatarPostSelector({ onShareAsPost }) {
  const [creations, setCreations] = useState([]);
  const [cursor, setCursor] = useState(null);

  // Store selected records in selection order.
  const [selected, setSelected] = useState([]);

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [planLoading, setPlanLoading] = useState(true);
  const [continuing, setContinuing] = useState(false);

  const [imageLimit, setImageLimit] = useState(null);
  const [planLabel, setPlanLabel] = useState('your plan');

  const [loadError, setLoadError] = useState('');
  const [moreError, setMoreError] = useState('');
  const [planError, setPlanError] = useState('');
  const [shareError, setShareError] = useState('');
  const [notice, setNotice] = useState('');

  const [collectionAttempt, setCollectionAttempt] = useState(0);
  const [planAttempt, setPlanAttempt] = useState(0);

  const mountedRef = useRef(false);
  const moreBusyRef = useRef(false);
  const continueBusyRef = useRef(false);
  const selectedRef = useRef([]);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Load the first collection page independently of the plan.
  useEffect(() => {
    let cancelled = false;

    async function loadCreations() {
      setLoading(true);
      setLoadError('');
      setMoreError('');

      try {
        const data = await listAvatarCreations();

        if (cancelled) return;

        if (!Array.isArray(data?.creations)) {
          throw new Error('Could not read your avatar collection.');
        }

        const unique = new Map();

        for (const item of data.creations) {
          unique.set(item._id, item);
        }

        setCreations([...unique.values()]);
        setCursor(data.nextCursor || null);
      } catch (error) {
        if (!cancelled) {
          setLoadError(errorMessage(error));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadCreations();

    return () => {
      cancelled = true;
    };
  }, [collectionAttempt]);

  // Fetch the actual allowance before enabling selection.
  useEffect(() => {
    let cancelled = false;

    async function loadPlan() {
      setPlanLoading(true);
      setPlanError('');

      try {
        const { data } = await api.get('/plans/me');

        if (cancelled) return;

        const plan = data?.plan;
        const limit = Number(plan?.limits?.postImagesMax);

        if (!Number.isSafeInteger(limit) || limit < 0) {
          throw new Error('Could not read your image allowance.');
        }

        setImageLimit(limit);
        setPlanLabel(plan?.label || 'your plan');
      } catch (error) {
        if (!cancelled) {
          setImageLimit(null);
          setPlanError(errorMessage(error));
        }
      } finally {
        if (!cancelled) {
          setPlanLoading(false);
        }
      }
    }

    loadPlan();

    return () => {
      cancelled = true;
    };
  }, [planAttempt]);

  function toggleSelection(item) {
    if (
      planLoading ||
      planError ||
      imageLimit === null ||
      continueBusyRef.current
    ) {
      return;
    }

    setNotice('');
    setShareError('');

    const current = selectedRef.current;
    const alreadySelected = current.some(
      (entry) => entry._id === item._id
    );

    let next;

    if (alreadySelected) {
      next = current.filter((entry) => entry._id !== item._id);
    } else {
      if (current.length >= imageLimit) {
        setNotice(
          `${planLabel} allows up to ${imageLimit} images per post.`
        );
        return;
      }

      if (!item.imageUrl) {
        setNotice('This avatar has no image available to share.');
        return;
      }

      next = [...current, item];
    }

    selectedRef.current = next;
    setSelected(next);
  }

  async function loadMore() {
    if (
      !cursor ||
      loading ||
      moreBusyRef.current ||
      continueBusyRef.current
    ) {
      return;
    }

    moreBusyRef.current = true;
    setLoadingMore(true);
    setMoreError('');

    try {
      const data = await listAvatarCreations({ cursor });

      if (!mountedRef.current) return;

      if (!Array.isArray(data?.creations)) {
        throw new Error('Could not read more avatars.');
      }

      setCreations((current) => {
        const existingIds = new Set(
          current.map((item) => item._id)
        );

        return [
          ...current,
          ...data.creations.filter((item) => {
            if (existingIds.has(item._id)) return false;

            existingIds.add(item._id);
            return true;
          }),
        ];
      });

      setCursor(data.nextCursor || null);
    } catch (error) {
      if (mountedRef.current) {
        setMoreError(errorMessage(error));
      }
    } finally {
      moreBusyRef.current = false;

      if (mountedRef.current) {
        setLoadingMore(false);
      }
    }
  }

  async function continueToPost() {
    if (
      continueBusyRef.current ||
      loading ||
      loadingMore ||
      planLoading ||
      planError ||
      imageLimit === null ||
      selectedRef.current.length === 0 ||
      selectedRef.current.length > imageLimit ||
      typeof onShareAsPost !== 'function'
    ) {
      return;
    }

    continueBusyRef.current = true;
    setContinuing(true);
    setShareError('');

    try {
      // Pass only what the composer integration needs.
      // This callback does not publish anything.
      await onShareAsPost(
        selectedRef.current.map((item) => ({
          _id: item._id,
          name: item.name,
          imageUrl: item.imageUrl,
        }))
      );
    } catch (error) {
      if (mountedRef.current) {
        setShareError(errorMessage(error));
      }
    } finally {
      continueBusyRef.current = false;

      if (mountedRef.current) {
        setContinuing(false);
      }
    }
  }

  const selectionReady =
    !planLoading &&
    !planError &&
    imageLimit !== null;

  const canContinue =
    selectionReady &&
    !loading &&
    !loadingMore &&
    !continuing &&
    selected.length > 0 &&
    selected.length <= imageLimit &&
    typeof onShareAsPost === 'function';

  return (
    <section
      className="avatar-post-selector"
      aria-label="Select avatars to share"
      aria-busy={continuing}
    >
      <div className="avatar-post-selector__scroll">
        <div className="avatar-post-selector__intro">
          <h2>Choose your avatars</h2>

          <p>
            Select avatars to attach to a post. You can add a
            caption on the next screen.
          </p>

          {planLoading ? (
            <p role="status">Checking your image allowance…</p>
          ) : planError ? (
            <div className="avatar-post-selector__error">
              <p role="alert">{planError}</p>

              <button
                type="button"
                className="avatar-post-selector__retry"
                onClick={() => setPlanAttempt((value) => value + 1)}
              >
                Retry allowance
              </button>
            </div>
          ) : (
            <p
              className="avatar-post-selector__count"
              role="status"
            >
              {selected.length} of {imageLimit} selected
              {' · '}
              {planLabel}
            </p>
          )}

          {notice && (
            <p
              className="avatar-post-selector__notice"
              role="status"
            >
              {notice}
            </p>
          )}

          {shareError && (
            <p
              className="avatar-post-selector__error"
              role="alert"
            >
              {shareError}
            </p>
          )}
        </div>

        {loading ? (
          <p role="status">Loading your avatars…</p>
        ) : loadError ? (
          <div className="avatar-post-selector__error">
            <p role="alert">{loadError}</p>

            <button
              type="button"
              className="avatar-post-selector__retry"
              onClick={() =>
                setCollectionAttempt((value) => value + 1)
              }
            >
              Retry loading avatars
            </button>
          </div>
        ) : creations.length === 0 ? (
          <p className="avatar-post-selector__empty">
            No saved avatars yet. Save an avatar in Studio
            before sharing it.
          </p>
        ) : (
          <>
            <div className="avatar-post-selector__grid">
              {creations.map((item) => {
                const selectedIndex = selected.findIndex(
                  (entry) => entry._id === item._id
                );

                const isSelected = selectedIndex !== -1;

                return (
                  <button
                    key={item._id}
                    type="button"
                    className={`avatar-post-selector__card ${
                      isSelected ? 'is-selected' : ''
                    }`}
                    aria-pressed={isSelected}
                    aria-label={
                      isSelected
                        ? `${item.name}, selected ${
                            selectedIndex + 1
                          }. Tap to deselect.`
                        : `Select ${item.name}`
                    }
                    disabled={!selectionReady || continuing}
                    onClick={() => toggleSelection(item)}
                  >
                    <span className="avatar-post-selector__preview">
                      <img
                        src={item.imageUrl}
                        alt=""
                        width="512"
                        height="512"
                        loading="lazy"
                        className="avatar-post-selector__image"
                      />

                      <span
                        className="avatar-post-selector__check"
                        aria-hidden="true"
                      >
                        {isSelected && (
                          <>
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <path
                                d="m5 12 4 4L19 6"
                                stroke="currentColor"
                                strokeWidth="3"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>

                            <span>{selectedIndex + 1}</span>
                          </>
                        )}
                      </span>
                    </span>

                    <span className="avatar-post-selector__name">
                      {item.name}
                    </span>
                  </button>
                );
              })}
            </div>

            {moreError && (
              <p
                className="avatar-post-selector__error"
                role="alert"
              >
                {moreError}
              </p>
            )}

            {cursor && (
              <button
                type="button"
                className="avatar-post-selector__more"
                disabled={loadingMore || continuing}
                onClick={loadMore}
              >
                {loadingMore
                  ? 'Loading…'
                  : moreError
                    ? 'Retry loading more'
                    : 'Load more'}
              </button>
            )}
          </>
        )}
      </div>

      <div className="avatar-post-selector__footer">
        <button
          type="button"
          className="avatar-post-selector__share"
          disabled={!canContinue}
          onClick={continueToPost}
        >
          {continuing
            ? 'Please wait…'
            : `Share as post · ${selected.length}`}
        </button>
      </div>
    </section>
  );
}
